import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { layoffService } from '_services';

export const LAYOFF_PERMISSION = 'Manage Layoffs';
// Payroll staff can see the tracker and review/finalize signed lay-offs
export const LAYOFF_REVIEW_PERMISSION = 'Review Layoffs';

// Status values match App\Enums\LayoffStatusEnum on the backend
export const LAYOFF_STATUSES = [
  { value: 1, label: 'Awaiting superintendent', fg: '#1d4f7a', bg: '#e3eef8' },
  { value: 2, label: 'Awaiting employee', fg: '#7a4f00', bg: '#fdf1dc' },
  { value: 3, label: 'Completed', fg: '#1f5e1a', bg: '#e1f0de' },
  { value: 4, label: 'Refused to sign', fg: '#5b3a8a', bg: '#efe7f8' },
  { value: 5, label: 'Cancelled', fg: '#4e4e4e', bg: '#eeeeee' },
  { value: 6, label: 'Void', fg: '#4e4e4e', bg: '#eeeeee' },
  { value: 9, label: 'Awaiting review', fg: '#0f5f66', bg: '#dcf1f2' },
  { value: 10, label: 'Delivery failed', fg: '#8f1f1c', bg: '#f9e0df' },
];

export const getLayoffStatus = (value) =>
  LAYOFF_STATUSES.find((status) => status.value === value) ||
  LAYOFF_STATUSES[0];

/**
 * Turn an axios error from the layoff API into a message for a toast.
 * Validation errors come back as { message: { field: [errors] } }.
 */
export const getErrorMessage = (error) => {
  const message = error?.response?.data?.message;
  if (message && typeof message === 'object') {
    return Object.values(message).flat().join(' ');
  }
  return message || 'Something went wrong. Please try again.';
};

/**
 * Retry server errors once, but show "not found" or "no access" straight away
 */
export const retryServerErrors = (failureCount, error) =>
  (error?.response?.status ?? 500) >= 500 && failureCount < 1;

export const layoffKeys = {
  all: ['layoffs'],
  access: ['layoffs', 'access'],
  list: (params) => ['layoffs', 'list', params],
  candidates: (params) => ['layoffs', 'candidates', params],
  signers: ['layoffs', 'signers'],
  history: (hash) => ['layoffs', 'history', hash],
};

/**
 * What the current user can do with lay-offs: manage, review, send, payroll.
 * Payroll (an email on the lay-off Payroll list) has no permission but uses the tracker.
 */
export const useLayoffAccess = (enabled = true) =>
  useQuery(layoffKeys.access, layoffService.getAccess, {
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useLayoffList = (params) =>
  useQuery(layoffKeys.list(params), () => layoffService.getLayoffs(params), {
    keepPreviousData: true,
  });

export const useCandidates = (params, enabled) =>
  useQuery(
    layoffKeys.candidates(params),
    () => layoffService.getCandidates(params),
    { enabled, keepPreviousData: true },
  );

export const useSigners = (enabled = true) =>
  useQuery(layoffKeys.signers, layoffService.getSigners, {
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useLayoffHistory = (hash) =>
  useQuery(layoffKeys.history(hash), () => layoffService.getHistory(hash), {
    enabled: !!hash,
  });

/**
 * Field errors from a 422 validation response, keyed by field name, or null
 */
export const getFieldErrors = (error) => {
  const data = error?.response?.data;
  return data?.validation_error && typeof data.message === 'object'
    ? data.message
    : null;
};

/**
 * Mutation that shows the API message in a toast and refreshes every layoff query.
 * With showFieldErrors the caller shows validation errors next to the fields instead.
 */
export const useLayoffMutation = (
  mutationFn,
  { showFieldErrors = false, ...options } = {},
) => {
  const queryClient = useQueryClient();
  return useMutation(mutationFn, {
    ...options,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(layoffKeys.all);
      if (data?.message) {
        toast.success(data.message);
      }
      options.onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      if (!(showFieldErrors && getFieldErrors(error))) {
        toast.error(getErrorMessage(error));
      }
      options.onError?.(error, variables);
    },
  });
};

export const useSendLayoffs = (options) =>
  useLayoffMutation(layoffService.sendLayoffs, options);

export const useRemindLayoff = (options) =>
  useLayoffMutation(layoffService.remindLayoff, options);

export const useCancelLayoff = (options) =>
  useLayoffMutation(layoffService.cancelLayoff, options);

export const useReassignSigner = (options) =>
  useLayoffMutation(layoffService.reassignSigner, options);

export const useRecordRefusal = (options) =>
  useLayoffMutation(layoffService.recordRefusal, options);

export const useVoidLayoff = (options) =>
  useLayoffMutation(layoffService.voidLayoff, options);

export const useResendEmail = (options) =>
  useLayoffMutation(layoffService.resendEmail, options);

/**
 * Download the finalized form and save it. Refreshes the layoff queries so the
 * "PDF downloaded" entry shows in the history.
 */
export const useDownloadPdf = () => {
  const queryClient = useQueryClient();
  return useMutation(layoffService.downloadPdf, {
    onSuccess: ({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      queryClient.invalidateQueries(layoffKeys.all);
    },
    onError: () => {
      toast.error('The PDF could not be downloaded. Please try again.');
    },
  });
};
